import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import TodoTemplateModal from "@/components/todoTemplate/todoTemplateModal";
import Restricted from "@/consumer/restricted";
import { Tooltip } from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import LanguageIcon from "@mui/icons-material/Language";
import { useNavigate } from "react-router-dom";
import { replaceDate } from "@/helper/dateHandling";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

export default function TodoTemplateMainTable() {
  const { t } = useTranslation();
  usePageHeading(t("todo_templates"));
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreate = () => {
    setModalMode("create");
    setSelectedRow(null);
    setModalOpen(true);
  };

  const handleEdit = (row: any) => {
    setModalMode("edit");
    setSelectedRow(row.original);
    setModalOpen(true);
  };

  const handleModalSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const navigateToTodoTemplateTranslationEdit = (row: any) => {
    navigate(`/todoTemplate/${row.original.id}/translation`);
  };

  const customRowActions = (row: any) => {
    return (
      <>
        <Restricted permissionRequired="frontend_view_entity_translation">
          <Tooltip
            arrow
            placement="top"
            enterDelay={1000}
            enterNextDelay={250}
            title={t("translation_edit")}
          >
            <GenericButton
              variant="icon"
              onClick={() => navigateToTodoTemplateTranslationEdit(row)}
            >
              <LanguageIcon />
            </GenericButton>
          </Tooltip>
        </Restricted>
      </>
    );
  };

  return (
    <>
      <GenericCrudTable
        key={refreshKey}
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        entity={"todoTemplate"}
        customRowActions={customRowActions}
        customCreateHandler={handleCreate}
        customEditHandler={handleEdit}
        disableEditButtonForRow={(row: any) =>
          row?.original?.name === "offener_punkt_aus_der_gefaehrdungsbeurteilung"
        }
        columns={[
          { header: t("id"), accessorKey: "id", id: "id" },
          {
            header: t("name"),
            accessorKey: "name",
            id: "name",
          },
          {
            header: t("title"),
            accessorKey: "title",
            id: "title",
          },
          {
            header: t("description"),
            accessorKey: "description",
            id: "description",
            Cell: ({ cell }: any) => {
              const value = cell.getValue();
              if (typeof value === "string") {
                return <span>{value}</span>;
              } else if (typeof value === "object" && value !== null) {
                return <span>{JSON.stringify(value)}</span>;
              }
              return <span>{String(value)}</span>;
            },
          },
          {
            header: t("created_on"),
            accessorKey: "created_on",
            sortingFn: "datetime",
            filterVariant: "date",
            id: "created_on",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("created_by"),
            accessorKey: "created_by",
            customColumnFilterId: "created_by_name",
            id: "created_by",
            Cell: ({ cell }: any) => {
              const val = cell.getValue();
              return <span>{val?.name || ""}</span>;
            },
          },
          {
            header: t("modified_on"),
            accessorKey: "modified_on",
            sortingFn: "modified_on",
            filterVariant: "date",
            id: "modified_on",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("modified_by"),
            accessorKey: "modified_by",
            customColumnFilterId: "modified_by_name",
            id: "modified_by",
            Cell: ({ cell }: any) => {
              const val = cell.getValue();
              return <span>{val?.name || ""}</span>;
            },
          },
        ]}
      />
      <TodoTemplateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        initialData={selectedRow}
        onSuccess={handleModalSuccess}
      />
    </>
  );
}
